import { env } from "@/config/env";
import { useAuthStore } from "@/stores/auth.store";

// ─── Réponses ───────────────────────────────────────────────────────────────
export interface ProductCopy {
	title: string;
	shortDescription: string;
	longDescription: string;
}
export interface SeoCopy {
	metaTitle: string;
	metaDescription: string;
	tags: string[];
}
export interface CategorySeoCopy {
	metaTitle: string;
	metaDescription: string;
	keywords: string[];
}
export interface RewriteResult {
	text: string;
}

// ─── Payloads ───────────────────────────────────────────────────────────────
export interface DescribeProductPayload {
	title: string;
	brand?: string | null;
	categoryName?: string | null;
	priceXAF?: number | null;
	attributes?: Record<string, unknown> | null;
	shortDescription?: string | null;
	longDescription?: string | null;
	instruction?: string | null;
}
export interface SeoPayload {
	title: string;
	brand?: string | null;
	categoryName?: string | null;
	shortDescription?: string | null;
	longDescription?: string | null;
	keywords?: string[] | null;
}
export interface CategorySeoPayload {
	name: string;
	parentName?: string | null;
	level?: number | null;
	description?: string | null;
	keywords?: string[] | null;
}
export interface RewritePayload {
	text: string;
	instruction?: string | null;
}

export type OnText = (delta: string) => void;

/**
 * Consomme un flux SSE depuis l'API IA. `onText` reçoit chaque fragment au fil
 * de l'eau ; la promesse résout avec l'objet final (`event: result`).
 */
async function streamAi<T>(
	path: string,
	payload: unknown,
	onText: OnText,
): Promise<T> {
	const token = useAuthStore.getState().accessToken;
	const res = await fetch(`${env.apiUrl}${path}`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		},
		body: JSON.stringify(payload),
	});

	if (res.status === 401) {
		useAuthStore.getState().logout();
		throw new Error("Session expirée, reconnecte-toi.");
	}
	if (!res.ok || !res.body) {
		let msg = `Erreur ${res.status}`;
		try {
			const j = (await res.json()) as { message?: string | string[] };
			msg = Array.isArray(j.message)
				? j.message.join(", ")
				: (j.message ?? msg);
		} catch {
			/* corps non JSON */
		}
		throw new Error(msg);
	}

	const reader = res.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	let result: T | null = null;
	let errorMsg: string | null = null;

	const handleBlock = (block: string): void => {
		let eventName = "message";
		const dataLines: string[] = [];
		for (const line of block.split("\n")) {
			if (line.startsWith("event:")) eventName = line.slice(6).trim();
			else if (line.startsWith("data:"))
				dataLines.push(line.slice(5).replace(/^ /, ""));
		}
		if (!dataLines.length) return;
		const data = dataLines.join("\n");
		if (eventName === "delta") {
			try {
				onText((JSON.parse(data) as { text?: string }).text ?? "");
			} catch {
				/* ignore */
			}
		} else if (eventName === "result") {
			try {
				result = JSON.parse(data) as T;
			} catch {
				/* ignore */
			}
		} else if (eventName === "error") {
			try {
				errorMsg =
					(JSON.parse(data) as { message?: string }).message ?? "Erreur IA.";
			} catch {
				errorMsg = "Erreur IA.";
			}
		}
	};

	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		let idx: number;
		while ((idx = buffer.indexOf("\n\n")) !== -1) {
			handleBlock(buffer.slice(0, idx));
			buffer = buffer.slice(idx + 2);
		}
	}
	if (buffer.trim()) handleBlock(buffer);

	if (errorMsg) throw new Error(errorMsg);
	if (result === null)
		throw new Error("L'assistant IA n'a pas renvoyé de résultat.");
	return result;
}

export const aiApi = {
	describeProductStream: (p: DescribeProductPayload, onText: OnText) =>
		streamAi<ProductCopy>("/admin/ai/product/describe", p, onText),
	generateSeoStream: (p: SeoPayload, onText: OnText) =>
		streamAi<SeoCopy>("/admin/ai/product/seo", p, onText),
	generateCategorySeoStream: (p: CategorySeoPayload, onText: OnText) =>
		streamAi<CategorySeoCopy>("/admin/ai/category/seo", p, onText),
	rewriteStream: (p: RewritePayload, onText: OnText) =>
		streamAi<RewriteResult>("/admin/ai/rewrite", p, onText),
};
